"use client";

import { useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShoppingCart,
  Menu,
  Search,
  Home,
  ShoppingBag,
  // Info,
  Mail,
  Phone,
  ChefHat,
  HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { useCart } from "@/context/CartContext";
import { useGetPublicSettingsQuery, useGetCategoriesQuery } from "@/store";

export default function Header() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getItemCount } = useCart();
  const { data: publicSettings } = useGetPublicSettingsQuery();
  const { data: categoriesData } = useGetCategoriesQuery({});
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") || "",
  );
  const cartCount = getItemCount();
  const defaultPhone = "09017104173";
  const phoneNumber = publicSettings?.data?.phone || defaultPhone;
  const phoneHref = `tel:${phoneNumber.replace(/\s+/g, "")}`;

  // Build dynamic category submenu from API data
  const categories = categoriesData?.data || [];
  const categorySubmenu = categories.map((category) => ({
    label: category.name,
    href: `/our-menu?category=${category.id}`,
  }));

  const handleSearch = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmedQuery = searchQuery.trim();
      if (trimmedQuery) {
        navigate(`/our-menu?search=${encodeURIComponent(trimmedQuery)}`);
        setIsMenuOpen(false);
      }
    },
    [searchQuery, navigate],
  );

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleSearch(e);
      }
    },
    [handleSearch],
  );

  const menuItems = [
    { label: "Home", href: "/", icon: Home },
    // { label: "About Us", href: "#about", icon: Info },
    {
      label: "Our Menu",
      href: "/our-menu",
      icon: ShoppingBag,
      submenu: categorySubmenu.length > 0 ? categorySubmenu : undefined,
    },
    { label: "Services", href: "/services", icon: ChefHat },
    { label: "FAQ", href: "/faq", icon: HelpCircle },
    // { label: "Track Order", href: "/track-order", icon: Package },
    { label: "Contact Us", href: "/contact", icon: Mail },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
    >
      <div className="container mx-auto px-4">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            <Link to="/" className="flex items-center gap-2">
              <img
                src="https://res.cloudinary.com/dtnerqsid/image/upload/v1769202362/zest-and-zeal/general/logo-white_1769202361916.png"
                alt="Zest and Zeal"
                className="h-30 w-40 dark:hidden"
              />
              <img
                src="https://res.cloudinary.com/dtnerqsid/image/upload/v1769202372/zest-and-zeal/general/logo-dark_1769202371922.png"
                alt="Zest and Zeal"
                className="h-30 w-40 hidden dark:block"
              />
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden items-center gap-8 md:flex">
            {menuItems.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                whileHover={{ y: -2 }}
              >
                <Link
                  to={item.href}
                  className="text-sm font-medium text-foreground/80 transition-colors hover:text-foreground"
                >
                  {item.label}
                </Link>
              </motion.div>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              className="hidden lg:flex items-center gap-2 text-sm text-muted-foreground"
            >
              <Phone className="h-4 w-4" />
              <a
                href={phoneHref}
                className="hover:text-foreground transition-colors"
              >
                {phoneNumber}
              </a>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              className="hidden md:block"
            >
              <form onSubmit={handleSearch} className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search products..."
                  className="w-[200px] pl-9 lg:w-[250px]"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                />
              </form>
            </motion.div>

            <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                onClick={() => navigate("/cart")}
              >
                <ShoppingCart className="h-5 w-5" />
                {cartCount > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-accent text-xs font-bold text-accent-foreground"
                  >
                    {cartCount}
                  </motion.span>
                )}
              </Button>
            </motion.div>

            <ThemeToggle />

            {/* Mobile Sidebar */}
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden">
                  <Menu className="h-6 w-6" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-[300px] sm:w-[400px] flex flex-col h-full p-0"
              >
                <SheetHeader className="shrink-0 p-6 pb-0">
                  <SheetTitle className="flex items-center gap-2">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
                      <span className="font-mono text-xl font-bold text-primary-foreground">
                        ZZ
                      </span>
                    </div>
                    <span className="text-xl font-bold">Zest and Zeal</span>
                  </SheetTitle>
                </SheetHeader>

                <div
                  className="flex-1 overflow-y-auto overscroll-contain px-6 mt-8"
                  style={{ WebkitOverflowScrolling: "touch" }}
                >
                  <div className="flex flex-col gap-6 pb-6">
                    {/* Search */}
                    <form onSubmit={handleSearch} className="relative">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="search"
                        placeholder="Search products..."
                        className="w-full pl-9"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                      />
                    </form>

                    <Separator />

                    {/* Navigation Links */}
                    <nav className="flex flex-col gap-2">
                      {/* <Link
                        to="/"
                        className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <Home className="h-5 w-5" />
                        Home
                      </Link> */}
                      {menuItems.map((item) => {
                        const Icon = item.icon;
                        return (
                          <div key={item.label}>
                            <Link
                              to={item.href}
                              className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
                              onClick={() => setIsMenuOpen(false)}
                            >
                              <Icon className="h-5 w-5" />
                              {item.label}
                            </Link>
                            {item.submenu && (
                              <div className="ml-8 mt-1 flex flex-col gap-1">
                                {item.submenu.map((subItem) => (
                                  <Link
                                    key={subItem.label}
                                    to={subItem.href}
                                    className="rounded-lg px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                                    onClick={() => setIsMenuOpen(false)}
                                  >
                                    {subItem.label}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </nav>

                    <Separator />

                    {/* Phone Number */}
                    <div className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm">
                      <Phone className="h-5 w-5 text-muted-foreground" />
                      <a
                        href={phoneHref}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {phoneNumber}
                      </a>
                    </div>

                    <Separator />

                    {/* Cart Button */}
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => {
                        navigate("/cart");
                        setIsMenuOpen(false);
                      }}
                    >
                      <ShoppingCart className="mr-3 h-5 w-5" />
                      Shopping Cart
                      {cartCount > 0 && (
                        <span className="ml-auto flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                          {cartCount}
                        </span>
                      )}
                    </Button>

                    {/* Theme Toggle */}
                    <div className="flex items-center justify-between rounded-lg border px-4 py-3">
                      <span className="text-sm font-medium">Theme</span>
                      <ThemeToggle />
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </motion.header>
  );
}
